import React, { useEffect, useState } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { fns } from "./firebase";
import { useSession } from "./auth.jsx";
import Queue from "./pages/Queue.jsx";
import Console from "./pages/Console.jsx";
import Gates from "./pages/Gates.jsx";
import Drift from "./pages/Drift.jsx";
import Studio from "./resident/Studio.jsx";
import Submit from "./resident/Submit.jsx";
import Journal from "./resident/Journal.jsx";
import Mindset from "./resident/Mindset.jsx";
import Wellbeing from "./pages/Wellbeing.jsx";
import Learn from "./resident/Learn.jsx";
import Seminar from "./pages/Seminar.jsx";
import Help from "./resident/Help.jsx";
import Sessions from "./pages/Sessions.jsx";
import Library from "./pages/Library.jsx";
import Specimens from "./pages/Specimens.jsx";
import Hunt from "./resident/Hunt.jsx";
import RegisterPage from "./pages/Register.jsx";
import Pods from "./pages/Pods.jsx";
import People from "./pages/People.jsx";
import AnnualPage from "./pages/Annual.jsx";

function Provisioning({ s }) {
  const [state, setState] = useState("checking");
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const call = httpsCallable(fns, "claimInvite");
        const r = await call({});
        if (r.data.ok || r.data.already) {
          setState("welcome");
          await s.refresh(); // pulls the fresh role claim + profile without a sign-out
        } else {
          setState("none");
        }
      } catch (e) {
        setErr(e.message); setState("error");
      }
    })();
  }, []);

  if (state === "checking") return (
    <div className="wrap" style={{ maxWidth: 520, paddingTop: 80 }}>
      <h1 style={{ letterSpacing: ".3em", fontSize: 22 }}>ATELIER</h1>
      <p style={{ marginTop: 16, color: "var(--graphite)" }}>Checking for your invitation…</p>
    </div>
  );
  if (state === "welcome") return (
    <div className="wrap" style={{ maxWidth: 520, paddingTop: 80 }}>
      <h1 style={{ letterSpacing: ".3em", fontSize: 22 }}>ATELIER</h1>
      <p style={{ marginTop: 16 }}>Invitation found. Setting up your studio…</p>
    </div>
  );
  if (state === "error") return (
    <div className="wrap" style={{ maxWidth: 520, paddingTop: 60 }}>
      <h1 style={{ fontSize: 20 }}>Something went wrong claiming your invitation</h1>
      <div className="warn" style={{ marginTop: 12 }}>{err}</div>
      <button className="btn" style={{ marginTop: 16 }} onClick={() => window.location.reload()}>Try again</button>
    </div>
  );
  return (
    <div className="wrap" style={{ maxWidth: 540, paddingTop: 60 }}>
      <h1 style={{ fontSize: 20 }}>No invitation found for this account</h1>
      <p style={{ marginTop: 10 }}>
        Signed in as <strong>{s.user.email}</strong>. Invitations are matched to the exact email address they
        were sent to — if yours went to a different address, sign out and use that account. If you believe
        you should have access, contact your Studio Director with the email above.
      </p>
      <button className="btn ghost" style={{ marginTop: 16 }} onClick={s.signOut}>Sign out</button>
    </div>
  );
}

function Tab({ to, children }) {
  return <NavLink to={to} className={({ isActive }) => (isActive ? "on" : "")}>{children}</NavLink>;
}

export default function App() {
  const s = useSession();
  const loc = useLocation();

  // THE REGISTER and THE ANNUAL are the public surfaces: reachable signed-out, by design.
  if (loc.pathname.startsWith("/register")) return <RegisterPage />;
  if (loc.pathname.startsWith("/annual")) return <AnnualPage />;

  if (!s.ready) return <div className="wrap" style={{ padding: 40, color: "var(--graphite)" }}>Opening the studio…</div>;

  if (!s.user) {
    return (
      <div className="wrap" style={{ maxWidth: 460, paddingTop: 80 }}>
        <h1 style={{ letterSpacing: ".3em", fontSize: 22 }}>ATELIER</h1>
        <p className="eyebrow" style={{ marginTop: 6 }}>Studio</p>
        <p style={{ margin: "18px 0" }}>Sign in with the account issued to you at Matriculation.</p>
        <button className="btn" onClick={s.signIn} disabled={s.signingIn}>
          {s.signingIn ? "Opening Google…" : "Sign in with Google"}
        </button>
        {s.authError && (
          <div className="warn" style={{ marginTop: 16 }}>
            <strong>Sign-in failed.</strong>
            <p style={{ margin: "6px 0 0", fontSize: 14 }}>{s.authError}</p>
          </div>
        )}
        <p style={{ marginTop: 20, fontSize: 12, color: "var(--graphite)" }}>
          If the button appears to do nothing, your browser blocked the popup — press it once more
          and it will switch to a full-page Google sign-in instead.
        </p>
      </div>
    );
  }

  if (!s.profile) return <Provisioning s={s} />;

  const resident = s.role === "resident";

  return (
    <>
      <header className="masthead">
        <div className="wrap row between" style={{ paddingTop: 14, paddingBottom: 14 }}>
          <div>
            <div style={{ fontFamily: "Archivo, sans-serif", fontWeight: 700, letterSpacing: ".32em" }}>ATELIER</div>
            <div style={{ fontSize: 12, color: "#93a0ac" }}>{resident ? "Resident Studio" : "Studio Master Console"}</div>
          </div>
          <div className="mono" style={{ fontSize: 11, color: "#93a0ac", textAlign: "right" }}>
            <div>{s.profile?.name ?? s.user.email}</div>
            <div>{s.role}{s.profile?.podId ? ` · ${s.profile.podId}` : s.profile?.clusterId ? ` · ${s.profile.clusterId}` : ""}</div>
            <button className="btn ghost" style={{ color: "#93a0ac", borderColor: "#3a4652", marginTop: 6, padding: "4px 10px" }} onClick={s.signOut}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="wrap">
        <nav className="tabs">
          {resident ? (
            <>
              <Tab to="/learn">Learn</Tab>
              <Tab to="/studio">Briefs</Tab>
              <Tab to="/library">Library</Tab>
              <Tab to="/journal">Journal</Tab>
              <Tab to="/mindset">Mindset</Tab>
              <Tab to="/help">Help</Tab>
            </>
          ) : (
            <>
              <Tab to="/queue">Queue</Tab>
              <Tab to="/seminar">Seminar</Tab>
              <Tab to="/sessions">Sessions</Tab>
              <Tab to="/pods">Pods</Tab>
              <Tab to="/people">People</Tab>
              <Tab to="/studio">Briefs</Tab>
              <Tab to="/learn">Learn</Tab>
              <Tab to="/library">Library</Tab>
              <Tab to="/gates">Gates</Tab>
              <Tab to="/wellbeing">Wellbeing</Tab>
              {s.isSenior && <Tab to="/drift">Drift</Tab>}
            </>
          )}
        </nav>

        <Routes>
          <Route path="/" element={<Navigate to={resident ? "/studio" : "/queue"} replace />} />
          {resident ? (
            <>
              <Route path="/learn" element={<Learn />} />
              <Route path="/library" element={<Library />} />
              <Route path="/studio" element={<Studio />} />
              <Route path="/brief/:briefId" element={<Submit />} />
              <Route path="/journal" element={<Journal />} />
              <Route path="/mindset" element={<Mindset />} />
              <Route path="/help" element={<Help />} />
              <Route path="/hunt" element={<Hunt />} />
            </>
          ) : (
            <>
              <Route path="/queue" element={<Queue />} />
              <Route path="/seminar" element={<Seminar />} />
              <Route path="/sessions" element={<Sessions />} />
              <Route path="/pods" element={<Pods />} />
              <Route path="/people" element={<People />} />
              <Route path="/studio" element={<Studio />} />
              <Route path="/brief/:briefId" element={<Submit />} />
              <Route path="/learn" element={<Learn />} />
              <Route path="/library" element={<Library />} />
              <Route path="/mark/:subId" element={<Console />} />
              <Route path="/gates" element={<Gates />} />
              <Route path="/wellbeing" element={<Wellbeing />} />
              <Route path="/drift" element={s.isSenior ? <Drift /> : <Navigate to="/queue" replace />} />
            </>
          )}
          <Route path="*" element={<Navigate to={resident ? "/studio" : "/queue"} replace />} />
        </Routes>

        <footer style={{ color: "var(--graphite)", fontSize: 12, padding: "40px 0" }}>
          {resident
            ? "Your ledger is part of the work, not paperwork about the work."
            : "No mark reaches a resident without a human. Drafts are staff-only and are never released."}
        </footer>
      </div>
    </>
  );
}
